import {
  CheckIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type { Source } from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useState } from 'react'

interface EditState {
  id: string
  name: string
  base_url: string
}

export default function SourcesPage() {
  const [sources, setSources] = useState<Source[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editing, setEditing] = useState<EditState | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newBaseUrl, setNewBaseUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('sources').select('*').order('name')
      setSources(data ?? [])
      setIsLoading(false)
    }
    load()
  }, [])

  async function handleAdd() {
    setError(null)
    if (!newName.trim() || !newBaseUrl.trim()) {
      setError('Name and base URL are required.')
      return
    }
    setSaving(true)
    const { data, error: err } = await supabase
      .from('sources')
      .insert({ name: newName.trim(), base_url: newBaseUrl.trim() })
      .select()
      .single()
    if (err) {
      setError(err.message)
      setSaving(false)
      return
    }
    setSources((prev) => [...prev, data])
    setNewName('')
    setNewBaseUrl('')
    setIsAdding(false)
    setSaving(false)
  }

  async function handleSaveEdit() {
    if (!editing) return
    setError(null)
    if (!editing.name.trim() || !editing.base_url.trim()) {
      setError('Name and base URL are required.')
      return
    }
    setSaving(true)
    const { error: err } = await supabase
      .from('sources')
      .update({ name: editing.name.trim(), base_url: editing.base_url.trim() })
      .eq('id', editing.id)
    if (err) {
      setError(err.message)
      setSaving(false)
      return
    }
    setSources((prev) =>
      prev.map((s) =>
        s.id === editing.id
          ? { ...s, name: editing.name, base_url: editing.base_url }
          : s,
      ),
    )
    setEditing(null)
    setSaving(false)
  }

  async function handleDelete(id: string, name: string) {
    if (
      !confirm(
        `Delete source "${name}"? This will also remove all related chapters.`,
      )
    )
      return
    await supabase.from('sources').delete().eq('id', id)
    setSources((prev) => prev.filter((s) => s.id !== id))
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-bold text-gray-900 text-xl">Sources</h1>
        {!isAdding && (
          <button
            type="button"
            onClick={() => {
              setIsAdding(true)
              setError(null)
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add source
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-red-600 text-sm">
          {error}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Base URL
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isAdding && (
              <tr className="bg-blue-50">
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Source name"
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="url"
                    value={newBaseUrl}
                    onChange={(e) => setNewBaseUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleAdd}
                      disabled={saving}
                      className="rounded p-1 text-green-600 hover:bg-green-50"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAdding(false)
                        setError(null)
                      }}
                      className="rounded p-1 text-gray-500 hover:bg-gray-100"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {sources.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-2">
                  {editing?.id === s.id ? (
                    <input
                      type="text"
                      value={editing.name}
                      onChange={(e) =>
                        setEditing(
                          (prev) => prev && { ...prev, name: e.target.value },
                        )
                      }
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                    />
                  ) : (
                    <span className="font-medium text-gray-900">{s.name}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editing?.id === s.id ? (
                    <input
                      type="url"
                      value={editing.base_url}
                      onChange={(e) =>
                        setEditing(
                          (prev) =>
                            prev && { ...prev, base_url: e.target.value },
                        )
                      }
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                    />
                  ) : (
                    <a
                      href={s.base_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {s.base_url}
                    </a>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-2">
                    {editing?.id === s.id ? (
                      <>
                        <button
                          type="button"
                          onClick={handleSaveEdit}
                          disabled={saving}
                          className="rounded p-1 text-green-600 hover:bg-green-50"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-100"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({
                              id: s.id,
                              name: s.name,
                              base_url: s.base_url,
                            })
                          }
                          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(s.id, s.name)}
                          className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
